import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import Button from '../../../Button';
import CustomControl from '../../../CustomControl';
import DropdownMenu from '../../../DropdownMenu';
import ImagePreload from '../../../ImagePreload';
import InfoCircleIcon from '../../../../icons/InfoCircleIcon';
import { SystemPrintAssetGroups } from '../../../../../graphql/queries/system-print-assets';
import {
  getGroupForAssetImage,
  getGroupSmartMatchingCopy,
} from '../../../../../util/print-asset-groups';
import { getPrintAssetDisplayImageUrl } from '../../../../../util/print-assets';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }),
  onElementConfigurationChange: PropTypes.func.isRequired,
};

// Badge above the card for a background whose asset belongs to a SYSTEM print
// asset group: shows the Smart Color Matching state, allows toggling the
// group link on/off, and switching the previewed variant.
const CardDesignerBackgroundGroupBadge = ({
  element,
  onElementConfigurationChange,
}) => {
  const { data: { systemPrintAssetGroups = {} } = {} } = useQuery(
    SystemPrintAssetGroups,
    {
      variables: { print_asset_group_type: 'BACKGROUND' },
      fetchPolicy: 'cache-first',
    },
  );

  const cardElementConfiguration = element?.cardElementConfiguration;
  const currentAssetImageId = cardElementConfiguration?.asset_image?.id;
  const assetGroup = getGroupForAssetImage(
    systemPrintAssetGroups.records ?? [],
    currentAssetImageId,
  );
  const members = useMemo(
    () => assetGroup?.members?.records ?? [],
    [assetGroup],
  );

  const memberImageUrls = useMemo(
    () =>
      members
        .map(member => getPrintAssetDisplayImageUrl(member.print_asset))
        .filter(Boolean),
    [members],
  );

  if (!assetGroup) {
    return null;
  }

  const linkedGroupId = cardElementConfiguration?.print_asset_group?.id;
  const smartEnabled = !!linkedGroupId;

  const onToggleSmart = () =>
    onElementConfigurationChange({
      element,
      elementConfig: {
        print_asset_group_id: smartEnabled ? null : assetGroup.id,
      },
    });

  return (
    <div className="card-designer-background-group-badge-bar">
      <ImagePreload urls={memberImageUrls} />
      <DropdownMenu
        openOnHover
        placement="bottom"
        toggleClassName="card-designer-background-group-badge-toggle"
        dropdownClassName="card-designer-background-group-badge-dropdown"
        toggle={
          <span
            className={classNames({
              'card-designer-background-group-badge': true,
              disabled: !smartEnabled,
            })}
          >
            Smart Color Matching {smartEnabled ? 'Enabled' : 'Disabled'}
            {/* The pill reads as a static label without it — this is what
                signals there is a panel behind it. */}
            <InfoCircleIcon color="#ffffff" size={11} />
          </span>
        }
      >
        <div className="p-2">
          <CustomControl
            type="checkbox"
            id="card-designer-background-group-smart"
            checked={smartEnabled}
            onChange={onToggleSmart}
            label="Smart Color Matching"
          />
          <p className="text-xs text-light mb-1">
            {getGroupSmartMatchingCopy({ group: assetGroup, smartEnabled })}
          </p>
          <div className="card-designer-background-group-variants">
            {members.map(member => (
              <Button
                key={member.id}
                btnStyle="reset"
                aria-label={member.print_asset.name}
                className={classNames({
                  'card-designer-background-group-variant': true,
                  selected:
                    member.print_asset.asset_image?.id === currentAssetImageId,
                })}
                onClick={() =>
                  onElementConfigurationChange({
                    element,
                    elementConfig: {
                      existing_asset_image_id:
                        member.print_asset.asset_image.id,
                    },
                  })
                }
              >
                <img
                  src={getPrintAssetDisplayImageUrl(member.print_asset)}
                  alt=""
                />
              </Button>
            ))}
          </div>
        </div>
      </DropdownMenu>
    </div>
  );
};

CardDesignerBackgroundGroupBadge.propTypes = propTypes;

export default CardDesignerBackgroundGroupBadge;
